import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import LiveBadge from "./LiveBadge";
import VoteButton from "./VoteButton";
import VerifiedBadge from "./VerifiedBadge";

const NEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export default function VideoCard({ item, variant = "wide", live = false, progressPct, fullWidth = false, hideMeta = false }) {
  const to = live ? `/live/${item.id}` : `/watch/${item.id}`;
  const isWide = variant === "wide";
  const sizeClass = fullWidth
    ? (isWide ? "aspect-video w-full" : "aspect-[2/3] w-full")
    : (isWide
      ? "aspect-video w-[260px] sm:w-[300px] md:w-[380px]"
      : "aspect-[2/3] w-[130px] sm:w-[160px] md:w-[200px]");

  const [hover, setHover] = useState(false);
  const videoRef = useRef(null);
  const hoverTimer = useRef(null);

  const isNew = item.created_at && Date.now() - new Date(item.created_at).getTime() < NEW_WINDOW_MS;
  const isHot = (item.view_count || 0) > 2000;
  const previewSrc = item.preview_url || item.trailer_url || item.video_url;

  const onEnter = () => {
    hoverTimer.current = setTimeout(() => {
      setHover(true);
      // slight delay, then play
      setTimeout(() => { if (videoRef.current) videoRef.current.play().catch(() => {}); }, 100);
    }, 900);
  };
  const onLeave = () => {
    clearTimeout(hoverTimer.current);
    setHover(false);
    if (videoRef.current) { try { videoRef.current.pause(); videoRef.current.currentTime = 0; } catch {} }
  };

  return (
    <Link
      to={to}
      data-testid={`video-card-${item.id}`}
      data-tv-focus="true"
      className={`tv-card block snap-start shrink-0 ${sizeClass} relative rounded-xl overflow-hidden group focus:outline-none focus-visible:ring-2 focus-visible:ring-tv-accent`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <img
        src={item.thumbnail_url || item.creator_avatar}
        alt={item.title}
        loading="lazy"
        className={`w-full h-full object-cover transition-opacity duration-500 ${hover && previewSrc ? "opacity-0" : "opacity-100"}`}
      />
      {hover && previewSrc && (
        <video
          ref={videoRef}
          src={previewSrc}
          muted
          playsInline
          loop
          data-testid={`card-preview-${item.id}`}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-80 group-hover:opacity-95 transition-opacity pointer-events-none" />

      {live && <div className="absolute top-2 left-2 z-10"><LiveBadge /></div>}
      {item.rating && item.rating !== "G" && !live && (
        <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold uppercase px-1.5 py-0.5 rounded tracking-wider z-10">{item.rating}</div>
      )}
      {/* AI-generated thumbnail badge — appears on the bottom-left
          corner so creators can spot at a glance which thumbnails were
          auto-generated by Nano Banana vs ones they uploaded themselves.
          Subtle (10% opacity until hover) so it never competes with the
          title overlay. */}
      {item.thumbnail_source === "ai_nano_banana" && !live && (
        <div
          data-testid={`video-card-ai-badge-${item.id}`}
          className="absolute bottom-2 left-2 bg-black/55 backdrop-blur-sm text-white/80 text-[8px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded opacity-40 group-hover:opacity-100 transition z-10 inline-flex items-center gap-1"
          title="Thumbnail auto-generated by AI — open the video to regenerate"
        >
          <span className="w-1 h-1 rounded-full bg-tv-accent inline-block" /> AI
        </div>
      )}

      <div className="absolute top-2 right-2 flex flex-col gap-1 items-end z-10">
        {!live && <VoteButton videoId={item.id} compact />}
        {item.is_premium && !live && (
          <div className="bg-tv-accent text-black text-[10px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Premium</div>
        )}
        {item.members_only && !live && (
          <div data-testid={`members-badge-${item.id}`} className="bg-gradient-to-r from-tv-accent to-yellow-500 text-black text-[10px] font-bold uppercase px-2 py-0.5 rounded tracking-wider flex items-center gap-1">
            <span>👑</span> Members
          </div>
        )}
        {!live && isNew && (
          <div className="bg-tv-brand text-black text-[10px] font-bold uppercase px-2 py-0.5 rounded tracking-wider tv-pulse">New</div>
        )}
        {!live && !isNew && isHot && (
          <div className="bg-tv-live text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">🔥 Hot</div>
        )}
        {item.quality_badges?.map((b) => (
          <div key={b} className="bg-black/80 border border-white/20 text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded tracking-wider">{b}</div>
        ))}
        {item.is_external && item.source_type && (
          <div data-testid={`source-badge-${item.id}`} className="bg-black/80 border border-tv-accent/40 text-tv-accent text-[9px] font-bold uppercase px-1.5 py-0.5 rounded tracking-wider">
            From {item.source_type}
          </div>
        )}
      </div>

      {!hideMeta && (
        <div className="absolute inset-x-0 bottom-0 p-3 z-10">
          <div className="text-[10px] uppercase tracking-[0.15em] text-tv-accent/90 font-semibold mb-0.5">
            {item.category_name || item.category}
          </div>
          <div className="font-display font-bold text-white text-base leading-tight line-clamp-2">{item.title}</div>
          <div className="text-xs text-white/60 mt-0.5 flex items-center gap-1">
            {item.creator_name}
            {item.creator_is_top ? <VerifiedBadge gold size={12} /> : item.creator_verified && <VerifiedBadge size={12} />}
          </div>
          {typeof progressPct === "number" && progressPct > 0 && (
            <div className="tv-progress mt-2"><div style={{ width: `${Math.min(100, progressPct)}%`, background: "#EF4444" }} /></div>
          )}
        </div>
      )}
      {hideMeta && typeof progressPct === "number" && progressPct > 0 && (
        // Still render the resume progress bar for "Continue Watching",
        // even when the title overlay is suppressed (titles render below).
        <div className="absolute inset-x-2 bottom-2 z-10">
          <div className="tv-progress"><div style={{ width: `${Math.min(100, progressPct)}%`, background: "#EF4444" }} /></div>
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="w-12 h-12 rounded-full bg-tv-accent text-black flex items-center justify-center shadow-xl">
          <Play className="w-5 h-5 fill-current" />
        </div>
      </div>
    </Link>
  );
}
